import React, { useCallback } from 'react';
import { prop } from 'ramda';

import { Button } from 'js/components/common/Button';
import ErrorMessage from 'js/components/common/Fields/ErrorMessage';
import { Input } from 'js/components/common/Fields/Input';
import Form from 'js/components/common/Form';

import { useTranslations } from 'js/hooks/i18n';
import { storageService } from 'js/services/storage.service';
import { useUserSelector } from 'js/store/hooks';

import { useValidationConfig } from '../hooks';

import { tagChangeOptions, verifyPinChange } from './server';

import style from './VerificationCardPin.scss';

export const VerificationCardPin = ({ closePopup, isShowDoneState, setIsShowDoneState }) => {
    const translate = useTranslations('playtech.mobile.system.verifycardpin.');
    const previousLoginTime = useUserSelector(prop('previousLoginTime'));
    const { validationConfig, setErrorPin } = useValidationConfig();

    const onSubmit = useCallback(async ({ pin }) => {
        try {
            await tagChangeOptions();
        } catch (error) {
            if (error.response.data.errorCode === 199) {
                try {
                    await verifyPinChange(pin);

                    try {
                        await tagChangeOptions();

                        setIsShowDoneState(true);

                        return;
                    } catch {
                        return translate('error.general');
                    }
                } catch (verifyError) {
                    if (verifyError.response.data.errorCode === 22) {
                        return translate('error.playerblocked');
                    }

                    setErrorPin({ error: true, value: pin });

                    return;
                }
            }

            return translate('error.general');
        }
    }, [setIsShowDoneState, translate, setErrorPin]);

    const handleSkip = () => {
        storageService.local.setItem('pinValidationSkipped', previousLoginTime);
        closePopup();
    };

    const handleClose = () => {
        storageService.local.setItem('pinValidationSkipped', 'done');
        closePopup();
    };

    return (
        <Form
            onSubmit={onSubmit}
            validationConfig={validationConfig}
            render={({ submitting, submitError, handleSubmit }) => (
                <form onSubmit={handleSubmit}>
                    {submitError && <ErrorMessage error={submitError} animate isFormError />}

                    {isShowDoneState ? (
                        <>
                            <span className={style.text}>{translate('popup.text.success')}</span>

                            <Button
                                onClick={handleClose}
                                size="small"
                                className={style.submit}
                            >
                                {translate('button.close')}
                            </Button>
                        </>
                    ) : (
                        <>
                            <span className={style.text}>{translate('popup.text')}</span>

                            <Input
                                type="password"
                                name="pin"
                                pattern="\d*"
                                inputmode="numeric"
                                className={style.field}
                                placeholder={translate('placeholder.pin')}
                            />

                            <Button
                                type="submit"
                                size="small"
                                className={style.submit}
                                disabled={submitting}
                            >
                                {translate('button.next')}
                            </Button>

                            <Button
                                view="link"
                                size="small"
                                data-testid="VerificationCardPin__skipBtn"
                                className={style.skip}
                                onClick={handleSkip}
                            >
                                {translate('button.skip')}
                            </Button>
                        </>
                    )}
                </form>
            )}
        />
    );
};
